import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import { AppointmentTypes } from '../../../types/appointment.types';

/**
 * Location cell for appointment tables.
 *
 * - Shows truncated address text (max ~200px) with ellipsis
 * - Missing location: red pin-slash icon + "No location set"
 * - Missing GPS (check-in exists but no coords): red pin-slash icon + "No GPS recorded"
 */

interface Props {
    /** Location string (appointment address) OR GPS location object */
    location?: string | AppointmentTypes.GpsLocation;
    /** If true, shows "No GPS recorded" instead of "No location set" when missing */
    isGps?: boolean;
}

export const LocationCell: React.FC<Props> = ({ location, isGps = false }) => {
    const theme = useTheme();

    // Determine display text
    let displayText: string | null = null;

    if (typeof location === 'string' && location.trim()) {
        displayText = location;
    } else if (location && typeof location === 'object' && 'latitude' in location) {
        displayText = location.address || `${location.latitude}, ${location.longitude}`;
    }

    if (!displayText) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOffIcon
                    sx={{
                        fontSize: 16,
                        color: theme.palette.error.main,
                    }}
                />
                <Typography
                    variant="body2"
                    sx={{ color: theme.palette.error.main }}
                >
                    {isGps ? 'No GPS recorded' : 'No location set'}
                </Typography>
            </Box>
        );
    }

    return (
        <Typography
            variant="body2"
            sx={{
                color: 'text.secondary',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
            title={displayText}
        >
            {displayText}
        </Typography>
    );
};
